import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Check } from 'lucide-react';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { useCapabilities } from '@/api/hooks';
import { WorkflowRail } from '@/components/WorkflowRail';
import { Button, EmptyState, Panel, PanelHeader, Stamp, Loading } from '@/components/ui';
import { cn } from '@/lib/cn';
import { say } from '@/lib/vocabulary';
import { Facts } from '@/components/Facts';
import { PackageContents, readContents } from '@/components/PackageContents';
import { readGate } from '@/components/GateChecks';
import { shippingState } from '@/pages/shipping';
import { PackagingSteps } from './PackagingSteps';

/**
 * Live packaging. The backend flipped the packaging gate on 2026-08-02, so
 * this replaces the build-time gated placeholder on the live route.
 *
 * The run and its state are declared schemas and are rendered as such. The
 * per-step payloads are AnyDataEnvelope in the contract, so they are shown
 * exactly as the API returns them rather than reshaped into a structure the
 * contract does not promise. Nothing here is fixture data.
 */

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/** The recorded packaging states, in the order the backend advances them. */
const STATES = [
  'DISCOVERING',
  'AXIS_PENDING',
  'STRUCTURING',
  'NAMING',
  'DOCUMENTING',
  'EXPORTABLE',
  'GATE_PENDING',
  'GATE_PASSED',
  'DELIVERING',
  'DELIVERED',
] as const;

// The two ways a run ends badly. They are deliberately absent from STATES,
// which is the happy ladder, so the strip below draws them as their own
// terminal chip rather than leaving every rung reading "not reached" while the
// banner says the run is over.
const TERMINAL_BAD = new Set(['GATE_FAILED', 'DELIVERY_FAILED']);


export function PackagingLive() {
  const { bucketId } = useParams();
  const qc = useQueryClient();
  const caps = useCapabilities();
  const gate = caps.data?.capabilities.packaging;

  // The run lives in the URL. There is no route that lists a bucket's
  // packaging runs, so a run held only in component state is lost the moment
  // an operator navigates away - and a half-finished package is exactly the
  // thing they need to come back to. In the URL it also shares.
  const [params, setParams] = useSearchParams();
  // The backend lists a bucket's runs now, so an in-progress package is found
  // by asking rather than by whatever this browser remembered. The URL still
  // wins when present, because a shared link should open the run it names.
  const runList = useQuery({
    queryKey: ['packaging-runs', bucketId],
    queryFn: ({ signal }) => backend.listPackagingRuns(bucketId!, signal),
    enabled: Boolean(bucketId),
    staleTime: 15_000,
  });
  const openRun = (
    (runList.data as { items?: { id: string; supersededBy?: unknown }[] } | undefined)?.items ?? []
  ).find((r) => !r.supersededBy);
  const runId = params.get('run') ?? openRun?.id ?? null;
  // Exports are listed by the backend now. The URL ids are still honoured so
  // an older shared link keeps working, but a built artefact is no longer lost
  // simply because the tab was closed.
  const exportList = useQuery({
    queryKey: ['packaging-exports', params.get('run') ?? openRun?.id ?? null],
    queryFn: ({ signal }) => backend.listPackagingExports((params.get('run') ?? openRun?.id)!, signal),
    enabled: Boolean(params.get('run') ?? openRun?.id),
    staleTime: 15_000,
  });
  const listedExportIds = (
    (exportList.data as { items?: { id?: string; exportId?: string }[] } | undefined)?.items ?? []
  )
    .map((e) => e.id ?? e.exportId)
    .filter((x): x is string => Boolean(x));
  const exportIds = [
    ...new Set([...(params.get('exports') ?? '').split(',').filter(Boolean), ...listedExportIds]),
  ];
  const setRunId = (id: string) => setParams({ run: id }, { replace: true });
  const addExport = (id: string) => {
    if (!runId || exportIds.includes(id)) return;
    setParams({ run: runId, exports: [...exportIds, id].join(',') }, { replace: true });
  };
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useQuery({
    queryKey: ['packaging-run', runId],
    queryFn: ({ signal }) => backend.getPackagingRun(runId!, signal),
    enabled: Boolean(runId),
    retry,
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s && (s === 'DELIVERED' || TERMINAL_BAD.has(s)) ? false : 6000;
    },
  });
  const discovery = useQuery({
    queryKey: ['packaging-discovery', runId],
    queryFn: ({ signal }) => backend.getPackagingDiscovery(runId!, signal),
    enabled: Boolean(runId),
    retry,
  });
  // The rail's advice turns on whether this run holds anything, and the gate
  // is where that fact lives. Reading it computes checks live and records no
  // transition, so it costs a query and decides nothing.
  const railGate = useQuery({
    queryKey: ['packaging-gate', runId],
    queryFn: ({ signal }) => backend.getPackagingGate(runId!, signal),
    enabled: Boolean(runId),
    retry,
  });
  const railShipping = shippingState(readGate((railGate.data as { data?: unknown })?.data ?? railGate.data));
  // 0078 lets a delivery finish while its run stays at GATE_PASSED, so the
  // delivery record is read directly rather than inferred from the run.
  const railDeliveryQ = useQuery({
    queryKey: ['packaging-delivery', runId],
    queryFn: ({ signal }) => backend.getPackagingDelivery(runId!, signal),
    enabled: Boolean(runId),
    retry,
  });
  const railDelivery =
    ((railDeliveryQ.data as { data?: { state?: string } })?.data ?? (railDeliveryQ.data as { state?: string } | undefined))
      ?.state ?? null;

  const start = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await backend.startPackagingRun(bucketId!, newIdempotencyKey());
      setRunId(r.id);
      void qc.invalidateQueries({ queryKey: ['packaging-run'] });
    } catch (e) {
      setNote(
        e instanceof ApiError
          ? explainLine(e, 'read this package')
          : 'The API did not respond.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (caps.isPending) return <Loading what="Reading this package" rows={4} />;

  if (gate !== 'IMPLEMENTED')
    return (
      <EmptyState
        title="Packaging is not available"
        description={`The backend advertises packaging as ${String(gate).toLowerCase()}. This surface enables itself the moment that gate reports implemented; nothing is shown from a fixture in the meantime.`}
      />
    );

  /**
   * The run this page is about, and what to do when there is not one.
   *
   * `run.isPending` and `run.isError` were read nowhere. A failed read left
   * `current` undefined, and the page carried on: ten state chips with none
   * marked, a shipping table reading "none", and no step card, which is a
   * plausible-looking screen describing a run nobody could read. The chips in
   * particular are the trap, because "no chip is current" is exactly how this
   * strip draws a run that has not started.
   */
  const current = run.data;
  const runUnreadable = Boolean(runId) && run.isError;
  const runLoading = Boolean(runId) && run.isPending;
  const stateIndex = current ? STATES.indexOf(current.state as (typeof STATES)[number]) : -1;

  return (
    <div className="min-h-0 flex-1 animate-pop space-y-3 overflow-y-auto p-5">
      {/* The trail up, and the top of it is this bucket. It used to keep
          climbing to a catalogue of every other one. */}
      <div className="flex flex-wrap items-center gap-2 text-small text-ink-3">
        <Link to={`/buckets/${bucketId}`} className="hover:text-ink">This bucket</Link>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="text-ink">Build the package</span>
        {current && (
          <Stamp tone={TERMINAL_BAD.has(current.state) ? 'bad' : current.state === 'DELIVERED' ? 'ok' : 'mut'} className="ml-2">
            {say(current.state)}
          </Stamp>
        )}
      </div>

      {/* The page identified itself only through a 12px breadcrumb. */}
      {/* The rail directly below this heading calls this stage "Build the
          package", and the heading called it something else, so the first two
          things a reader sees disagreed about what page they were on. */}
      <h1 className="text-h2 text-ink">Build the package</h1>

      {runId && (
        <div className="flex flex-wrap items-center gap-3 text-small text-ink-2">
          <span>
            Every decision you make here is recorded against this package. If it gets stuck, start another; your files
            are untouched and every decision stays on the record.
          </span>
          <Button variant="ghost" className="ml-auto" onClick={() => void start()} disabled={busy}>
            {busy ? 'Starting…' : 'Start a new run'}
          </Button>
        </div>
      )}

      {!runId ? (
        <Panel>
          <PanelHeader
            title="Start a packaging run"
            sub="takes stock of everything in the bucket, then walks you through what to ship and how it is organised"
          />
          <div className="flex flex-wrap items-center gap-3 px-4 py-4">
            <Button onClick={() => void start()} disabled={busy} loading={busy}>
              {busy ? 'Starting…' : 'Start packaging run'}
            </Button>
            {note && <span className="text-small text-warn">{note}</span>}
          </div>
        </Panel>
      ) : (
        <>
          {/* The whole journey, the same six stages as the bucket page, so an
              operator deep in packaging still sees where they are overall. The
              per-state rail below zooms into this one stage. */}
          <p className="rounded-md border border-line bg-panel px-4 py-2.5 text-caption text-ink-3">
            <span className="font-semibold uppercase tracking-wider text-ink-3">Scope</span> A package covers one
            bucket. A company with several buckets is handed over as several packages, each with its own name,
            documents and release checks. The company page combines them under a single name.
          </p>
          <WorkflowRail
            bucketId={bucketId ?? null}
            facts={{
              hasContext: true,
              known: true,
              observed: true,
              observing: false,
              sealed: true,
            // This page only exists for a run whose walk sealed and covered
            // something; a seal over nothing never reaches packaging.
            sealedObjects: null,
            listedObjects: null,
              walking: false,
              // This page does not read the toolset. Zero would be a claim.
              toolCount: 0,
              // This page does not read detections; a run already exists here,
              // so the confirm-first advice belongs on the bucket page.
              promotable: 0,
              supported: 0,
              packagingState: current?.state ?? null,
              exportsBuilt: exportIds.length > 0,
              // Null until the gate has been read: an unknown population must
              // never be drawn as an empty one.
              populationRegistered: railShipping.known ? railShipping.willShip : null,
              deliveryState: railDelivery,
            }}
          />
          <Panel>
            <PanelHeader title="This package" sub="where this one has got to inside the packaging stage" />
            {runLoading && (
              <p role="status" className="border-b border-line px-4 py-2 text-small text-ink-3">
                Reading this package.
              </p>
            )}
            {runUnreadable && (
              <p role="alert" className="border-b border-line px-4 py-2 text-small text-warn">
                {explainLine(run.error, 'read this package')} Nothing below describes this run: the ladder shows where a
                package would be, and this one could not be read.
              </p>
            )}
            <div className={cn('flex flex-wrap gap-1.5 px-4 py-3', (runLoading || runUnreadable) && 'opacity-50')}>
              {/* A run that ended badly is at no rung of the happy ladder, so
                  stateIndex is -1 and every chip read "not reached" while the
                  banner underneath said the run was over. The rungs it did
                  clear are still true and are drawn as cleared. */}
              {STATES.map((s, i) => {
                const failed = current ? TERMINAL_BAD.has(current.state) : false;
                // GATE_FAILED comes after the gate was reached; DELIVERY_FAILED
                // after delivery began. Both cleared everything before them.
                const reached = failed
                  ? STATES.indexOf(current!.state === 'GATE_FAILED' ? 'GATE_PENDING' : 'DELIVERING')
                  : stateIndex;
                const done = reached > i;
                const here = reached === i;
                return (
                  <span
                    key={s}
                    className={cn(
                      'flex items-center gap-1.5 rounded border px-2 py-1 text-caption',
                      done && 'border-transparent bg-ok/10 text-ok',
                      here && 'border-acc bg-acc-soft text-acc-2',
                      !done && !here && 'border-line bg-panel-2 text-ink-4',
                    )}
                  >
                    {done && <Check className="h-3 w-3" aria-hidden="true" />}
                    {say(s)}
                  </span>
                );
              })}
              {current && TERMINAL_BAD.has(current.state) && (
                <span className="flex items-center gap-1.5 rounded border border-bad bg-bad-soft px-2 py-1 text-caption text-bad">
                  {say(current.state)}
                </span>
              )}
            </div>
            {current && TERMINAL_BAD.has(current.state) && (
              <p className="border-t border-line px-4 py-2 text-small text-warn">
                This run ended at {say(current.state)}. The backend records the reason on the run's events; a new run is
                started, and this one is kept as a record.
              </p>
            )}
          </Panel>

          <Panel>
            <PanelHeader title="What you are shipping" sub="read from the contents of each file" />
            {(() => {
              const raw = (discovery.data as { data?: unknown } | null)?.data ?? discovery.data;
              const contents = readContents(raw);
              return contents ? (
                <PackageContents contents={contents} />
              ) : (
                <Facts value={raw} pending={discovery.isPending} error={discovery.isError ? discovery.error : undefined} />
              );
            })()}
          </Panel>

          {current && (
            <PackagingSteps
              runId={runId}
              bucketId={current.bucketId}
              stateChangedAt={current?.stateChangedAt}
            state={current.state}
              discovery={discovery}
              exportIds={exportIds}
              onExportCreated={addExport}
            />
          )}
        </>
      )}
    </div>
  );
}
